'use client'

import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material'
import { isArray, isFinite as isFiniteLodash, isNumber, isString } from 'lodash'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import ApexChart from '~/components/charting/ApexChart'

import type { AssistantDashboardPanel } from '~/features/assistant/streamTypes'
import type { ApexOptions } from '~/components/charting/ApexChart'

type Props = {
  panel: AssistantDashboardPanel
}

type SeriesDef = { key: string; label?: string; axis?: 'left' | 'right' }
type Row = Record<string, unknown>
type KeyedRow = { rowKey: string; values: Row }

export default function GenerativePanelCard({ panel }: Props) {
  const t = useTranslations('generativeDashboard.page')

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {panel.title ? (
          <Typography variant="subtitle1" fontWeight={600}>
            {panel.title}
          </Typography>
        ) : null}

        <Box sx={{ flexGrow: 1 }}>
          <PanelBody panel={panel} />
        </Box>

        {panel.summaryText ? (
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {panel.summaryText}
          </Typography>
        ) : null}

        <ProvenanceAccordion provenance={panel.provenance} label={t('howBuilt')} />
      </CardContent>
    </Card>
  )
}

function PanelBody({ panel }: Props) {
  const t = useTranslations('generativeDashboard.page')
  const config = panel.panelConfig ?? {}

  // The tile's layout is known but its data is still loading: show a placeholder shaped like the eventual panel.
  if (panel.status === 'pending') {
    return <PanelSkeleton panelType={panel.panelType} />
  }

  switch (panel.panelType) {
    case 'kpi_card':
      return <KpiPanel config={config} />
    case 'line_chart':
      return <LinePanel config={config} emptyLabel={t('noData')} />
    case 'bar_chart':
      return <BarPanel config={config} emptyLabel={t('noData')} />
    case 'status_donut':
      return <DonutPanel config={config} emptyLabel={t('noData')} />
    case 'data_table':
      return <TablePanel config={config} title={panel.title} emptyLabel={t('noData')} exportLabel={t('exportCsv')} />
    case 'narrative':
    default:
      return panel.summaryText ? null : <Typography color="text.secondary">{t('noData')}</Typography>
  }
}

/** Loading placeholder shaped to match the eventual panel type, shown while a tile's data is still being fetched. */
function PanelSkeleton({ panelType }: { panelType: string }) {
  if (panelType === 'kpi_card') {
    return (
      <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 3, height: '100%' }}>
        <Skeleton variant="text" width="50%" sx={{ fontSize: '3rem' }} />
        <Skeleton variant="text" width="40%" />
      </Stack>
    )
  }
  if (panelType === 'data_table') {
    return (
      <Stack spacing={1} sx={{ pt: 1 }}>
        {['r0', 'r1', 'r2', 'r3', 'r4'].map((rowKey) => (
          <Skeleton key={rowKey} variant="rectangular" height={28} />
        ))}
      </Stack>
    )
  }
  if (panelType === 'status_donut') {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: 300 }}>
        <Skeleton variant="circular" width={180} height={180} />
      </Stack>
    )
  }
  if (panelType === 'narrative') {
    return (
      <Stack spacing={1} sx={{ pt: 1 }}>
        <Skeleton variant="text" width="90%" />
        <Skeleton variant="text" width="80%" />
        <Skeleton variant="text" width="60%" />
      </Stack>
    )
  }
  return <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
}

function KpiPanel({ config }: { config: Row }) {
  const value = config.value
  const label = isString(config.label) ? config.label : ''
  const unit = isString(config.unit) ? config.unit : ''
  const deltaPct = isNumber(config.deltaPct) ? config.deltaPct : undefined
  const display = isNumber(value) ? formatNumber(value) : isString(value) ? value : '—'
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ py: 3, height: '100%' }}>
      <Typography variant="h3" component="div">
        {display}
        {unit ? ` ${unit}` : ''}
      </Typography>
      {label ? <Typography color="text.secondary">{label}</Typography> : null}
      {deltaPct !== undefined ? (
        <Typography variant="body2" color={deltaPct >= 0 ? 'success.main' : 'error.main'}>
          {deltaPct >= 0 ? '+' : ''}
          {formatNumber(deltaPct)}%
        </Typography>
      ) : null}
    </Stack>
  )
}

function LinePanel({ config, emptyLabel }: { config: Row; emptyLabel: string }) {
  const { series, options, hasData } = useTimeSeriesChart(config)
  if (!hasData) return <EmptyChart label={emptyLabel} />
  return <ApexChart type="line" height={300} series={series} options={options} />
}

function BarPanel({ config, emptyLabel }: { config: Row; emptyLabel: string }) {
  const { series, options, isEmpty } = useMemo(() => {
    const data = isArray(config.data) ? (config.data as Row[]) : []
    const xKey = isString(config.xKey) ? config.xKey : 'label'
    const yKey = isString(config.yKey) ? config.yKey : 'value'
    const categories = data.map((row) => String(row[xKey] ?? ''))
    return {
      series: [{ name: yKey, data: data.map((row) => toNumber(row[yKey])) }] as ApexOptions['series'],
      options: {
        chart: { toolbar: { show: false } },
        xaxis: { categories },
        dataLabels: { enabled: false }
      } as ApexOptions,
      isEmpty: data.length === 0
    }
  }, [config])
  if (isEmpty) return <EmptyChart label={emptyLabel} />
  return <ApexChart type="bar" height={300} series={series} options={options} />
}

function DonutPanel({ config, emptyLabel }: { config: Row; emptyLabel: string }) {
  const { series, options, isEmpty } = useMemo(() => {
    const segments = isArray(config.segments) ? (config.segments as Row[]) : []
    return {
      series: segments.map((s) => toNumber(s.value)) as ApexOptions['series'],
      options: { labels: segments.map((s) => String(s.label ?? '')), legend: { position: 'bottom' } } as ApexOptions,
      isEmpty: segments.length === 0
    }
  }, [config])
  if (isEmpty) return <EmptyChart label={emptyLabel} />
  return <ApexChart type="donut" height={300} series={series} options={options} />
}

function TablePanel({
  config,
  title,
  emptyLabel,
  exportLabel
}: {
  config: Row
  title?: string
  emptyLabel: string
  exportLabel: string
}) {
  const { columns, rows } = useMemo<{ columns: { field: string; headerName: string }[]; rows: KeyedRow[] }>(() => {
    const rawRows = isArray(config.rows) ? (config.rows as Row[]) : []
    const declared = isArray(config.columns) ? (config.columns as Row[]) : []
    const cols =
      declared.length > 0
        ? declared.map((c) => ({
            field: String(c.field ?? ''),
            headerName: isString(c.headerName) ? c.headerName : String(c.field ?? '')
          }))
        : rawRows[0]
          ? Object.keys(rawRows[0]).map((field) => ({ field, headerName: field }))
          : []
    const keyed = rawRows.map((row, index) => ({ rowKey: rowKeyFor(row, index), values: row }))
    return { columns: cols, rows: keyed }
  }, [config.columns, config.rows])

  if (rows.length === 0) return <Typography color="text.secondary">{emptyLabel}</Typography>

  return (
    <Stack spacing={1}>
      <Box sx={{ alignSelf: 'flex-end' }}>
        <Button size="small" onClick={() => exportCsv(title ?? 'dashboard', columns, rows.map((r) => r.values))}>
          {exportLabel}
        </Button>
      </Box>
      <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.field}>{col.headerName}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.rowKey}>
                {columns.map((col) => (
                  <TableCell key={col.field}>{formatCell(row.values[col.field])}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  )
}

function EmptyChart({ label }: { label: string }) {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ height: 300 }}>
      <Typography color="text.secondary">{label}</Typography>
    </Stack>
  )
}

function ProvenanceAccordion({ provenance, label }: { provenance: unknown; label: string }) {
  if (!provenance || (isArray(provenance) && provenance.length === 0)) return null
  return (
    <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, bgcolor: 'transparent' }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1.5, minHeight: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5 }}>
        <Box
          component="pre"
          sx={{ m: 0, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.secondary' }}
        >
          {JSON.stringify(provenance, null, 2)}
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

/** Builds Apex series/options for a multi-series line chart with either a time or category x-axis. */
function useTimeSeriesChart(config: Row) {
  return useMemo(() => {
    const data = isArray(config.data) ? (config.data as Row[]) : []
    const xKey = isString(config.xKey) ? config.xKey : 'x'
    const isTime = config.xType === 'time'
    const seriesDefs: SeriesDef[] = isArray(config.series)
      ? (config.series as Row[]).map((s) => ({
          key: String(s.key ?? ''),
          label: isString(s.label) ? s.label : undefined,
          axis: s.axis === 'right' ? 'right' : s.axis === 'left' ? 'left' : undefined
        }))
      : [{ key: 'y', label: 'value' }]

    const series: ApexOptions['series'] = seriesDefs.map((def) => ({
      name: def.label ?? def.key,
      data: data.map((row) => {
        const y = toNumber(row[def.key])
        if (isTime) {
          return { x: toTimestamp(row[xKey]), y }
        }
        return { x: String(row[xKey] ?? ''), y }
      })
    }))

    const options: ApexOptions = {
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { curve: 'smooth', width: 2 },
      dataLabels: { enabled: false },
      xaxis: { type: isTime ? 'datetime' : 'category' },
      legend: { position: 'top' }
    }

    const yaxis = buildDualAxes(seriesDefs, data)
    if (yaxis) {
      options.yaxis = yaxis
    }

    return { series, options, hasData: data.length > 0 }
  }, [config])
}

/**
 * Builds a dual-axis config when series belong on different scales: either the model assigned
 * axis 'right' explicitly, or — as a safety net for exactly two unassigned series — their
 * magnitudes differ by ~an order of magnitude (e.g. kWh vs session counts).
 */
function buildDualAxes(seriesDefs: SeriesDef[], data: Row[]): ApexYAxis[] | undefined {
  const AUTO_SPLIT_RATIO = 8
  let axes = seriesDefs.map((def) => def.axis)

  if (axes.every((axis) => axis === undefined) && seriesDefs.length === 2) {
    const [maxA, maxB] = seriesDefs.map(
      (def) => Math.max(...data.map((row) => Math.abs(toNumber(row[def.key]))), 0) || 0
    ) as [number, number]
    if (maxA > 0 && maxB > 0 && (maxA / maxB >= AUTO_SPLIT_RATIO || maxB / maxA >= AUTO_SPLIT_RATIO)) {
      axes = maxA >= maxB ? ['left', 'right'] : ['right', 'left']
    }
  }

  if (!axes.includes('right')) return undefined

  // One Apex yaxis entry per series; same-side series share the side's anchor axis so we
  // don't stack duplicate scales.
  const leftAnchor = axes.findIndex((axis) => axis !== 'right')
  const rightAnchor = axes.findIndex((axis) => axis === 'right')
  return seriesDefs.map((def, index) => {
    const isRight = axes[index] === 'right'
    const anchor = isRight ? rightAnchor : leftAnchor
    const anchorDef = seriesDefs[anchor] ?? def
    return {
      seriesName: anchorDef.label ?? anchorDef.key,
      opposite: isRight,
      show: index === anchor,
      title: index === anchor ? { text: anchorDef.label ?? anchorDef.key } : undefined
    }
  })
}

function exportCsv(name: string, columns: { field: string; headerName: string }[], rows: Row[]) {
  const header = columns.map((c) => csvCell(c.headerName)).join(',')
  const body = rows.map((row) => columns.map((c) => csvCell(formatCell(row[c.field]))).join(',')).join('\n')
  const csv = `${header}\n${body}`
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${name.replace(/[^a-z0-9-_]+/gi, '_')}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

function csvCell(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (isNumber(value)) return formatNumber(value)
  if (isString(value)) return value
  return JSON.stringify(value)
}

function formatNumber(value: number): string {
  if (!isFiniteLodash(value)) return String(value)
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)
}

function toNumber(value: unknown): number {
  const num = Number(value)
  return isFiniteLodash(num) ? num : 0
}

/** Convert value to a numeric timestamp (epoch ms). Handles ISO strings and numeric values. */
function toTimestamp(value: unknown): number {
  if (isNumber(value)) {
    return value
  }
  if (isString(value)) {
    const parsed = Date.parse(value)
    if (isFiniteLodash(parsed)) {
      return parsed
    }
  }
  const num = Number(value)
  return isFiniteLodash(num) ? num : 0
}

/** A content-derived row key (id/name/first cell) so rows don't rely on array index for React keys. */
function rowKeyFor(row: Row, index: number): string {
  const candidate = row.id ?? row.key ?? row.name ?? Object.values(row)[0]
  const base = isString(candidate) || isNumber(candidate) ? String(candidate) : ''
  return base ? `${base}-${index}` : `row-${index}`
}
